import { deliveryRequestStrings } from '@data/deliveryRequest';
import type {
  DeliveryRequestForm,
  PostDeliveryRequest,
  DeliveryRequestListItem,
  DeliveryRequestBatchSentForm,
  DeliveryRequestBatchSubmitBody,
  DeliveryRequest,
} from '@models/next/DeliveryRequest';
import { DeliveryRequestLog } from '@models/next/DeliveryRequestLog';
import { DeliveryRequestStatusName } from '@models/next/DeliveryRequestStatus';
import type { RemoteFile } from '@models/next/FileType';
import { empty } from '@monoid-dev/mobx-zod-form';
import {
  MessagesEither,
  makeMessagesEitherLeftIf,
  makeMessagesEitherRightIf,
  mergeMessagesEither,
} from '@utils/errors';
import dayjs from 'dayjs';
import { find, findLast, maxBy, pipe } from 'remeda';

export type DeliveryRequestType = 'regular' | 'yokomochi';

export const deliveryRequestTypeMap: Partial<
  Record<DeliveryRequestStatusName, DeliveryRequestType>
> = {
  メール送信済未読: 'regular',
  CSV送信済: 'yokomochi',
};

// There's an loop dep between @data/DeliveryRequest and @models/DeliveryRequestHelper
export class DeliveryRequestHelper {
  static getFileName(v: DeliveryRequestListItem) {
    return `${deliveryRequestStrings.deliveryRequestFile}${
      v.delivery_request_no.split('TK')[1] ?? '01'
    }`;
  }

  static mapFormToPost(v: DeliveryRequestForm, context: { d2d_no: string }): PostDeliveryRequest {
    return {
      ...v,
      d2d_no: context.d2d_no,
      delivery_date: `${v.delivery_date_date} ${
        v.delivery_date_type === 'AM'
          ? '09:00'
          : v.delivery_date_type === 'PM'
          ? '18:00'
          : v.delivery_date_time
      }`,
    };
  }

  static mapListItemToForm(v: DeliveryRequestListItem): DeliveryRequestForm {
    return {
      ...v,
      delivery_date_date: dayjs(v.delivery_date).tz('Asia/Tokyo').format('YYYY-MM-DD'),
      delivery_date_time: dayjs(v.delivery_date).tz('Asia/Tokyo').format('HH:mm'),
    };
  }

  static mapBatchSentFormToBatchSubmitBody(
    v: DeliveryRequestBatchSentForm,
    context: { delivery_sent_file?: RemoteFile },
  ): DeliveryRequestBatchSubmitBody {
    return {
      time: dayjs(`${v.time_date} ${v.time_time}`).format(),
      delivery_sent_file: context.delivery_sent_file,
    };
  }

  static getMailSubmittedAt(v: DeliveryRequestListItem) {
    return findLast(v.status, (s) => s.name === 'メール送信済未読')?.time;
  }

  static getFirstMailSubmittedAt(v: DeliveryRequestListItem) {
    return find(v.status, (s) => s.name === 'メール送信済未読')?.time;
  }

  static isSent(v: DeliveryRequestListItem) {
    return v.delivery_status.at(-1)?.name === 'お届け完了';
  }

  static getType(v: DeliveryRequestListItem): DeliveryRequestType | undefined {
    const lastRequestStatus = findLast(v.status, (s) =>
      Object.keys(deliveryRequestTypeMap).includes(s.name),
    );

    if (!lastRequestStatus) {
      return undefined;
    }
    return deliveryRequestTypeMap[lastRequestStatus.name];
  }

  static getBatchSentFormInitialOutput(): DeliveryRequestBatchSentForm {
    return {
      time_date: dayjs().format('YYYY-MM-DD'),
      time_time: dayjs().format('HH:mm'),
      delivery_sent_file: empty,
    };
  }

  /**
   * @returns Whether it is possible to do any mutation to the case. Right now, for
   * 1. delivery_status
   * 2. append payment slip
   */
  static canMutate(v: Pick<DeliveryRequest, 'status' | 'pre_alert' | 'delivery_request_no'>) {
    return mergeMessagesEither(() =>
      makeMessagesEitherLeftIf(v.pre_alert.is_ledger_locked, {
        messages: [deliveryRequestStrings.errorPreAlertLedgerLocked],
      }),
    );
  }

  /**
   * @returns Whetier it is possible to edit the delivery request content.
   */
  static canEdit(
    v: Pick<DeliveryRequest, 'status' | 'pre_alert' | 'delivery_request_no'>,
  ): MessagesEither {
    return mergeMessagesEither(
      () => this.canMutate(v),
      () =>
        makeMessagesEitherLeftIf(v.status.at(-1)?.name === '配車完了', {
          messages: [
            deliveryRequestStrings.errorDeliveryRequestsStatusComplete(v.delivery_request_no),
          ],
        }),
    );
  }

  /**
   * @returns Whether it is possible to submit the delivery request to truck companies,
   * be it メール送信, CSV送信済, 横持ち送信済 or 配車リンク
   */
  static canSubmit(
    v: Pick<DeliveryRequest, 'status' | 'pre_alert' | 'delivery_request_no'>,
  ): MessagesEither {
    return mergeMessagesEither(
      () => this.canEdit(v),
      () =>
        makeMessagesEitherLeftIf(!v.pre_alert.is_manually_locked && !v.pre_alert.atd, {
          messages: [deliveryRequestStrings.errorCannotSubmit(v.delivery_request_no)],
        }),
    );
  }

  /**
   * @returns Whether it is possible to go to `配車完了`
   */
  static canComplete(v: DeliveryRequestListItem): MessagesEither {
    return makeMessagesEitherRightIf(
      pipe(
        v.status.at(-1)?.name,
        (s) =>
          s === '横持ち送信済' ||
          s === 'メール送信済未読' ||
          s === 'メール送信済既読' ||
          s === '配車リンク作成済',
      ),
      {
        messages: [deliveryRequestStrings.errorCannotComplete],
      },
    );
  }
  static getLastUpdateUserAndTime(
    v: DeliveryRequest,
    logs: DeliveryRequestLog[],
  ):
    | { fullname?: string; action: '送信済' | '一時保存' | 'リンク生成済'; time?: string }
    | undefined {
    const lastAction = findLast(
      v.status,
      (v) =>
        v.name === 'メール送信済未読' ||
        v.name === '配車リンク作成済' ||
        v.name === 'CSV送信済' ||
        v.name === '横持ち送信済',
    );

    const lastEdit = maxBy(logs, (log) => new Date(log.created_at).getTime());

    if (!lastAction && !lastEdit) {
      return {
        fullname: `${v.creator.last_name} ${v.creator.first_name}`,
        action: '一時保存',
        time: v.created_at,
      };
    } else {
      return (lastAction?.time ?? '') > (lastEdit?.created_at ?? '')
        ? {
            fullname: lastAction?.submitter?.fullname ?? undefined,
            action: lastAction?.name === '配車リンク作成済' ? 'リンク生成済' : '送信済',
            time: lastAction?.time,
          }
        : {
            fullname: lastEdit?.editor,
            action: '一時保存',
            time: lastEdit?.created_at,
          };
    }
  }
}
